/**
 * Pricing Context
 * 
 * Provides pricing configuration to all components.
 * Allows A/B testing different price points without duplicating code.
 * 
 * Scenario A (default): $249 listed → $199 sale (20% off)
 * Scenario B (/b):      $179 listed → $145 sale (20% off)
 * 
 * Accessories (Recovery Tag, Wireless Charger, Travel Pouch) are now
 * positioned as a "Free Welcome Kit" — no longer counted toward
 * a bundle value or discount calculation.
 */

import { createContext, useContext, type ReactNode } from "react";

export interface PricingConfig {
  /** Scenario identifier */
  scenarioId: string;
  /** Display name for the scenario */
  scenarioName: string;
  
  /** Listed (strikethrough) price of Ryder One */
  originalPrice: number;
  /** Sale price of Ryder One */
  salePrice: number;
  /** Discount percentage (displayed on badges, promo bar, CTAs) */
  discountPercent: number;
  /** Savings amount (originalPrice - salePrice) */
  savingsAmount: number;
  
  /** @deprecated Use discountPercent instead. Kept for backward compat. */
  deviceDiscount: number;
  /** @deprecated Use savingsAmount instead. Kept for backward compat. */
  deviceSavings: number;
  
  /**
   * Bundle fields are kept for backward compatibility but now mirror
   * the device-only pricing. The "bundle value" concept is removed —
   * accessories are a free welcome kit, not priced items.
   */
  bundleOriginalValue: number;
  bundleSalePrice: number;
  bundleSavings: number;
  bundleDiscount: number;
  
  /** Accessory prices — set to 0 since they're a free welcome kit */
  accessories: {
    recoveryTag: number;
    wirelessCharger: number;
    travelPouch: number;
  };
  
  /** Shopify discount code to apply at checkout (empty = no code) */
  discountCode: string;
  
  /** FAQ answer for "What's included in the box?" */
  faqBundleAnswer: string;
}

/** Scenario A: $249 listed → $199 sale (20% off) */
export const SCENARIO_A: PricingConfig = {
  scenarioId: "a",
  scenarioName: "Standard Pricing",
  
  originalPrice: 249,
  salePrice: 199,
  discountPercent: 20,
  savingsAmount: 50,
  
  // backward compat aliases
  deviceDiscount: 20,
  deviceSavings: 50,
  
  // Bundle fields now mirror device pricing (no separate bundle value)
  bundleOriginalValue: 249,
  bundleSalePrice: 199,
  bundleSavings: 50,
  bundleDiscount: 20,
  
  // Accessories are free — no dollar values displayed
  accessories: {
    recoveryTag: 0,
    wirelessCharger: 0,
    travelPouch: 0,
  },
  
  // Custom discount code for Variant A checkout
  discountCode: "2R11SK07QHTW",
  
  faqBundleAnswer: "Every Ryder One order includes the Ryder One hardware wallet, plus a free welcome kit: Recovery Tag, Wireless Charger, and Travel Pouch — all included at no extra cost. You pay $199 (regularly $249), that's 20% off.",
};

/** Scenario B: $179 listed → $145 sale (20% off) */
export const SCENARIO_B: PricingConfig = {
  scenarioId: "b",
  scenarioName: "Lower Price Point",
  
  originalPrice: 179,
  salePrice: 145,
  discountPercent: 20,
  savingsAmount: 34,
  
  // backward compat aliases
  deviceDiscount: 20,
  deviceSavings: 34,
  
  // Bundle fields mirror device pricing
  bundleOriginalValue: 179,
  bundleSalePrice: 145,
  bundleSavings: 34,
  bundleDiscount: 20,
  
  accessories: {
    recoveryTag: 0,
    wirelessCharger: 0,
    travelPouch: 0,
  },
  
  // Custom discount code for Variant B checkout
  discountCode: "Q5R7RB4R14BD",
  
  faqBundleAnswer: "Every Ryder One order includes the Ryder One hardware wallet, plus a free welcome kit: Recovery Tag, Wireless Charger, and Travel Pouch — all included at no extra cost. You pay $145 (regularly $179), that's 20% off.",
};

const PricingContext = createContext<PricingConfig>(SCENARIO_A);

export function PricingProvider({ 
  pricing, 
  children 
}: { 
  pricing: PricingConfig; 
  children: ReactNode; 
}) {
  return (
    <PricingContext.Provider value={pricing}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing(): PricingConfig {
  return useContext(PricingContext);
}

export default PricingContext;
